import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from "react";
import { Link } from "react-router-dom";

type ToastTone = "success" | "error" | "warning" | "delivery";

interface ToastAction {
  label: string;
  to: string;
}

interface ToastOptions {
  duration?: number;
  action?: ToastAction;
}

interface ToastItem {
  id: number;
  tone: ToastTone;
  message: string;
  action?: ToastAction;
}

interface ToastContextValue {
  show: (message: string, tone?: ToastTone, options?: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextValue>({ show: () => {} });

export const useToast = () => useContext(ToastContext);

const TONE_STYLES: Record<ToastTone, { wrap: string; icon: string }> = {
  success: {
    wrap: "bg-primary-500 text-background-50",
    icon: "ri-checkbox-circle-fill",
  },
  error: {
    wrap: "bg-red-500 text-white",
    icon: "ri-error-warning-fill",
  },
  warning: {
    wrap: "bg-accent-500 text-foreground-950",
    icon: "ri-alert-fill",
  },
  delivery: {
    wrap: "bg-accent-500 text-white",
    icon: "ri-e-bike-2-line",
  },
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: number) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const show = useCallback(
    (message: string, tone: ToastTone = "success", options?: ToastOptions) => {
      const id = Date.now() + Math.random();
      setItems((prev) => [
        ...prev,
        { id, tone, message, action: options?.action },
      ]);
      window.setTimeout(() => {
        setItems((prev) => prev.filter((item) => item.id !== id));
      }, options?.duration ?? 4000);
    },
    [],
  );

  return (
    <ToastContext.Provider value={{ show }}>
      {children}
      <div className="fixed bottom-6 left-6 z-[100] flex flex-col gap-2.5">
        {items.map((item) => {
          const toneStyle = TONE_STYLES[item.tone];
          return (
            <div
              key={item.id}
              className={`yc-toast pointer-events-auto flex items-start gap-3 rounded-card px-4 py-3.5 shadow-pop min-w-[280px] max-w-[400px] ${toneStyle.wrap}`}
            >
              <span className="w-6 h-6 flex items-center justify-center text-xl shrink-0 mt-0.5">
                <i className={toneStyle.icon}></i>
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold leading-6">
                  {item.message}
                </p>
                {item.action && (
                  <Link
                    to={item.action.to}
                    onClick={() => dismiss(item.id)}
                    className="mt-2 inline-flex items-center gap-1.5 rounded-btn bg-white/20 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/30 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-external-link-line"></i>
                    </span>
                    {item.action.label}
                  </Link>
                )}
              </div>
              <button
                type="button"
                onClick={() => dismiss(item.id)}
                className="w-6 h-6 flex items-center justify-center rounded-full text-white/80 hover:text-white hover:bg-white/20 transition-colors duration-200 cursor-pointer shrink-0"
                aria-label="إغلاق الإشعار"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}